import React, { useState, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
  submitDeliverable, 
  getStudentSubmission, 
  updateSubmissionFeedback,
  resubmitDeliverable,
  getSubmissionFileIcon,
  formatSubmissionFileSize,
  validateSubmissionFile
} from '../services/submissionService';
import FileTypeIcon from './FileTypeIcon';

const SubmissionManager = ({ 
  deliverable, 
  projectId, 
  userType = 'student',
  onSubmissionUpdate = () => {} 
}) => {
  const { currentUser } = React.useContext(AuthContext);
  
  // State management
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState(false);
  
  // Form state for new submission
  const [formData, setFormData] = useState({
    content: '',
    file: null
  });
  const [fileErrors, setFileErrors] = useState([]);
  const [error, setError] = useState('');
  
  // Feedback form state (for companies)
  const [feedbackData, setFeedbackData] = useState({
    feedback: '',
    status: 'approved'
  });

  // Load existing submission
  useEffect(() => {
    if (userType === 'student' && deliverable && currentUser) {
      loadStudentSubmission();
    } else {
      setLoading(false);
    }
  }, [deliverable?.id, currentUser?.uid, userType]);

  const loadStudentSubmission = async () => {
    try {
      setLoading(true);
      const existingSubmission = await getStudentSubmission(currentUser.uid, deliverable.id);
      setSubmission(existingSubmission);
    } catch (error) {
      console.error('Error loading submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const errors = validateSubmissionFile(file);
      setFileErrors(errors);
      
      if (errors.length === 0) {
        setFormData({ ...formData, file });
      } else {
        setFormData({ ...formData, file: null });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.content.trim()) {
      setError('Please provide submission notes');
      return;
    }

    if (fileErrors.length > 0) {
      setError('Please fix file errors before submitting');
      return;
    }

    try {
      setSubmitting(true);

      const submissionData = {
        deliverableId: deliverable.id,
        projectId,
        studentId: currentUser.uid,
        studentName: currentUser.displayName || 'Unknown Student',
        content: formData.content,
        deliverableTitle: deliverable.title
      };

      let submissionId;
      
      if (submission && submission.status === 'rejected') {
        // Resubmit if previously rejected
        submissionId = await resubmitDeliverable(submission.id, submissionData, formData.file);
      } else {
        // New submission
        submissionId = await submitDeliverable(submissionData, formData.file);
      }

      // Reset form
      setFormData({ content: '', file: null });
      
      // Reload submission
      await loadStudentSubmission();
      
      // Notify parent
      onSubmissionUpdate();

    } catch (error) {
      console.error('Error submitting:', error);
      setError(error.message || 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackData.feedback.trim()) {
      setError('Please provide feedback');
      return;
    }

    try {
      setSubmitting(true);
      
      await updateSubmissionFeedback(
        submission.id,
        feedbackData.feedback,
        feedbackData.status,
        currentUser.uid
      );

      // Reload submission
      await loadStudentSubmission();
      setFeedbackMode(false);
      setFeedbackData({ feedback: '', status: 'approved' });
      
      // Notify parent
      onSubmissionUpdate();

    } catch (error) {
      console.error('Error providing feedback:', error);
      setError(error.message || 'Failed to provide feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'needs_revision': return '#f59e0b';
      case 'submitted': return '#6366f1';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Needs Revision';
      case 'needs_revision': return 'Needs Revision';
      case 'submitted': return 'Under Review';
      default: return 'Not Submitted';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        Loading submission...
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    }}>
      {/* Submission Status */}
      {submission && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: `${getStatusColor(submission.status)}15`,
          borderRadius: '8px',
          borderLeft: `4px solid ${getStatusColor(submission.status)}`
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <h4 style={{ 
              margin: 0, 
              color: getStatusColor(submission.status),
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              {getStatusText(submission.status)}
            </h4>
            <span style={{ fontSize: '14px', color: '#666' }}>
              Version {submission.version || 1}
            </span>
          </div>
          
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Submitted: {formatDate(submission.submittedAt)}
          </div>

          {submission.content && (
            <div style={{ fontSize: '14px', marginBottom: '12px' }}>
              <strong>Notes:</strong> {submission.content}
            </div>
          )}

          {submission.fileUrl && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '12px'
            }}>
              <FileTypeIcon type={getSubmissionFileIcon(submission.fileType)} size={20} style={{ marginRight: 2 }} />
              <a 
                href={submission.fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: '#6366f1', 
                  textDecoration: 'none', 
                  fontWeight: '500' 
                }}
              >
                {submission.fileName}
              </a>
              {submission.fileSize && (
                <span style={{ fontSize: '12px', color: '#666' }}>
                  ({formatSubmissionFileSize(submission.fileSize)})
                </span>
              )}
            </div>
          )}

          {submission.feedback && (
            <div style={{ 
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'white',
              borderRadius: '6px'
            }}>
              <strong style={{ fontSize: '14px' }}>Feedback:</strong>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px', 
                lineHeight: '1.5' 
              }}>
                {submission.feedback}
              </p>
              {submission.reviewedAt && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  Reviewed: {formatDate(submission.reviewedAt)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Student Submission Form */}
      {userType === 'student' && (
        <>
          {(!submission || submission.status === 'rejected') && (
            <div>
              <h4 style={{ 
                marginBottom: '16px', 
                fontSize: '18px', 
                fontWeight: 'bold' 
              }}>
                {submission ? 'Resubmit Assignment' : 'Submit Assignment'}
              </h4>

              {error && (
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#dc2626',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Submission Notes *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Describe your submission, approach, challenges faced, etc..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      minHeight: '100px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Attach File (optional)
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '4px',
                    margin: '4px 0 0 0'
                  }}>
                    Supported: PDF, Word, Excel, PowerPoint, Images, Videos, ZIP (max 50MB)
                  </p>

                  {fileErrors.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      {fileErrors.map((error, index) => (
                        <div key={index} style={{ 
                          color: '#dc2626', 
                          fontSize: '12px',
                          marginBottom: '4px'
                        }}>
                          {error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || fileErrors.length > 0}
                  style={{
                    backgroundColor: submitting || fileErrors.length > 0 ? '#9ca3af' : '#6366f1',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: submitting || fileErrors.length > 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Submitting...' : (submission ? 'Resubmit Assignment' : 'Submit Assignment')}
                </button>
              </form>
            </div>
          )}

          {submission && submission.status === 'submitted' && (
            <div style={{ textAlign: 'center', color: '#6366f1' }}>
              <h4>Assignment Submitted Successfully!</h4>
              <p>Your assignment is being reviewed. You'll receive feedback soon.</p>
            </div>
          )}

          {submission && submission.status === 'approved' && (
            <div style={{ textAlign: 'center', color: '#10b981' }}>
              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', color: '#10b981' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M14.7 7.7a1 1 0 0 0-1.4-1.4L10 9.6 9.7 9.3a1 1 0 0 0-1.4 1.4l1 1a1 1 0 0 0 1.4 0l4-4z"/>
                </svg>
                Assignment Approved!
              </h4>
              <p>Great work! This assignment has been completed successfully.</p>
            </div>
          )}
        </>
      )}

      {/* Company Feedback Interface */}
      {userType === 'company' && submission && (
        <>
          {!feedbackMode && submission.status === 'submitted' && (
            <button
              onClick={() => setFeedbackMode(true)}
              style={{
                backgroundColor: '#6366f1',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Provide Feedback
            </button>
          )}

          {feedbackMode && (
            <div>
              <h4 style={{ 
                marginBottom: '16px', 
                fontSize: '18px', 
                fontWeight: 'bold' 
              }}>
                Provide Feedback
              </h4>

              {error && (
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#dc2626',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleFeedbackSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Decision
                  </label>
                  <select
                    value={feedbackData.status}
                    onChange={(e) => setFeedbackData({ ...feedbackData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  >
                    <option value="approved">Approve</option>
                    <option value="rejected">Request Revision</option>
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Feedback *
                  </label>
                  <textarea
                    value={feedbackData.feedback}
                    onChange={(e) => setFeedbackData({ ...feedbackData, feedback: e.target.value })}
                    placeholder="Provide detailed feedback on the submission..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      minHeight: '100px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    required
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      backgroundColor: submitting ? '#9ca3af' : '#6366f1',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFeedbackMode(false)}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#6b7280',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {/* No submission state for company */}
      {userType === 'company' && !submission && (
        <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
          <p>No submission yet from this student.</p>
        </div>
      )}
    </div>
  );
};

export default SubmissionManager; 